import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { authAPI } from '../../services/api'
import { setAuthToken, removeAuthToken } from '../../utils/auth'
import { resetTimer } from './timerSlice'
import { registrationErrorMessage } from '../../utils/registrationErrors'

export interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  role: string
  is_active: boolean
  is_verified: boolean
}

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('access_token'),
  refreshToken: localStorage.getItem('refresh_token'),
  isAuthenticated: false, // Always start as false - will be validated on app load
  isLoading: false,
  error: null,
}

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(email, password)
      return response.data
    } catch (error: any) {
      // Extract error message from the response
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Login failed'
      return rejectWithValue(errorMessage)
    }
  }
)

export const register = createAsyncThunk(
  'auth/register',
  async (userData: {
    email: string
    password: string
    first_name: string
    last_name: string
    phone_number?: string
    role?: string
  }, { rejectWithValue }) => {
    try {
      const response = await authAPI.register(userData)
      return response.data
    } catch (error) {
      return rejectWithValue(registrationErrorMessage(error))
    }
  }
)

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async () => {
    const response = await authAPI.getCurrentUser()
    return response.data
  }
)

export const validateToken = createAsyncThunk(
  'auth/validateToken',
  async (_, { getState }) => {
    const state = getState() as { auth: AuthState }
    const { token } = state.auth
    
    if (!token) {
      throw new Error('No token found')
    }
    
    try {
      const response = await authAPI.getCurrentUser()
      return response.data
    } catch (error) {
      // Token is invalid, clear it
      localStorage.removeItem('access_token')
      localStorage.removeItem('resume_access_token')
      localStorage.removeItem('refresh_token')
      throw error
    }
  }
)

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (refreshToken: string) => {
    const response = await authAPI.refreshToken(refreshToken)
    return response.data
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      // Clear user-specific profile data before clearing user info
      if (state.user?.id) {
        localStorage.removeItem(`userProfile_${state.user.id}`)
      }
      
      // Clear old generic profile data for backward compatibility
      localStorage.removeItem('userProfile')
      
      state.user = null
      state.token = null
      state.refreshToken = null
      state.isAuthenticated = false
      state.error = null
      localStorage.removeItem('access_token')
      localStorage.removeItem('resume_access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('timerState')
      removeAuthToken()
    },
    clearError: (state) => {
      state.error = null
    },
    setCredentials: (state, action: PayloadAction<{ user: User; access_token: string; refresh_token: string }>) => {
      const { user, access_token, refresh_token } = action.payload
      state.user = user
      state.token = access_token
      state.refreshToken = refresh_token
      state.isAuthenticated = true
      state.error = null
      localStorage.setItem('access_token', access_token)
      localStorage.setItem('refresh_token', refresh_token)
      setAuthToken(access_token)
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload.user
        state.token = action.payload.access_token
        state.refreshToken = action.payload.refresh_token
        state.isAuthenticated = true
        localStorage.setItem('access_token', action.payload.access_token)
        if (action.payload.resume_access_token) localStorage.setItem('resume_access_token', action.payload.resume_access_token)
        else localStorage.removeItem('resume_access_token')
        localStorage.setItem('refresh_token', action.payload.refresh_token)
        setAuthToken(action.payload.access_token)
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false
        // Extract error message from the response
        const errorMessage = (action.payload as any)?.detail || 
                           (action.payload as any)?.message || 
                           action.error?.message || 
                           'Login failed'
        state.error = errorMessage
      })
      
      // Register
      .addCase(register.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(register.fulfilled, (state) => {
        state.isLoading = false
        // User needs to login after registration
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false
        state.error = typeof action.payload === 'string' ? action.payload : 'Registration could not be completed. Please try again.'
      })
      
      // Get current user
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload
        state.isAuthenticated = true
      })
      .addCase(getCurrentUser.rejected, (state) => {
        // Clear user-specific profile data before clearing user info
        if (state.user?.id) {
          localStorage.removeItem(`userProfile_${state.user.id}`)
        }
        
        state.isLoading = false
        state.user = null
        state.isAuthenticated = false
        state.token = null
        state.refreshToken = null
        localStorage.removeItem('access_token')
      localStorage.removeItem('resume_access_token')
        localStorage.removeItem('refresh_token')
        removeAuthToken()
      })
      
      // Refresh token
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.access_token
        state.refreshToken = action.payload.refresh_token
        state.user = action.payload.user
        localStorage.setItem('access_token', action.payload.access_token)
        localStorage.setItem('refresh_token', action.payload.refresh_token)
        setAuthToken(action.payload.access_token)
      })
      .addCase(refreshToken.rejected, (state) => {
        // Clear user-specific profile data before clearing user info
        if (state.user?.id) {
          localStorage.removeItem(`userProfile_${state.user.id}`)
        }
        
        state.user = null
        state.token = null
        state.refreshToken = null
        state.isAuthenticated = false
        localStorage.removeItem('access_token')
      localStorage.removeItem('resume_access_token')
        localStorage.removeItem('refresh_token')
        removeAuthToken()
      })
      
      // Validate token
      .addCase(validateToken.pending, (state) => {
        state.isLoading = true
      })
      .addCase(validateToken.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload
        state.isAuthenticated = true
      })
      .addCase(validateToken.rejected, (state) => {
        // Clear user-specific profile data before clearing user info
        if (state.user?.id) {
          localStorage.removeItem(`userProfile_${state.user.id}`)
        }
        
        state.isLoading = false
        state.user = null
        state.isAuthenticated = false
        state.token = null
        state.refreshToken = null
        localStorage.removeItem('access_token')
      localStorage.removeItem('resume_access_token')
        localStorage.removeItem('refresh_token')
        removeAuthToken()
      })
  },
})

// Utility function to clear all profile data (for testing/debugging)
export const clearAllProfileData = () => {
  // Clear all user-specific profile data
  const keys = Object.keys(localStorage)
  keys.forEach(key => {
    if (key.startsWith('userProfile_')) {
      localStorage.removeItem(key)
    }
  })
  // Clear old generic profile data
  localStorage.removeItem('userProfile')
}

export const { logout, clearError, setCredentials } = authSlice.actions
export default authSlice.reducer
